import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, Shield, LineChart } from "lucide-react";

const STORAGE_KEY = "sw_cookie_consent_v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeConsent(consent) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, ts: Date.now() })); } catch {}
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (!c) {
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const acceptAll = () => {
    writeConsent({ technical: true, analytics: true });
    setVisible(false);
  };
  const rejectAll = () => {
    writeConsent({ technical: true, analytics: false });
    setVisible(false);
  };
  const save = () => {
    writeConsent({ technical: true, analytics });
    setShowPrefs(false);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md z-[80]"
        >
          <div className="glass gradient-border rounded-3xl p-6 md:p-7 shadow-[0_30px_80px_rgba(88,28,135,0.35)]">
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br from-purple-500/30 to-blue-500/30 border border-white/10 flex items-center justify-center text-purple-300">
                <Cookie size={18} />
              </span>
              <div className="min-w-0">
                <h3 className="font-display text-white text-lg">Rispettiamo la tua privacy</h3>
                <p className="mt-1 text-sm text-slate-300 leading-relaxed">
                  Utilizziamo cookie <strong>tecnici</strong> per il corretto funzionamento del sito e, con il tuo consenso, cookie <strong>analitici</strong> anonimi per capire come possiamo migliorare.{" "}
                  <Link to="/cookie" className="text-purple-300 hover:text-white underline underline-offset-2">Scopri di più</Link>.
                </p>
              </div>
            </div>

            {showPrefs && (
              <div className="mt-5 space-y-3" data-testid="cookie-preferences">
                <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <Shield size={16} className="mt-0.5 text-purple-300" />
                  <div className="flex-1">
                    <div className="text-sm text-white font-medium">Cookie tecnici <span className="ml-2 text-[10px] uppercase tracking-widest text-purple-300/80">Sempre attivi</span></div>
                    <p className="text-xs text-slate-400 mt-1">Necessari per il funzionamento del sito e la sicurezza. Non richiedono consenso.</p>
                  </div>
                  <div className="h-6 w-11 rounded-full bg-purple-500/40 border border-purple-400/50 flex items-center px-1 opacity-70">
                    <span className="h-4 w-4 rounded-full bg-white ml-auto" />
                  </div>
                </div>

                <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 cursor-pointer hover:border-white/25 transition">
                  <LineChart size={16} className="mt-0.5 text-blue-300" />
                  <div className="flex-1">
                    <div className="text-sm text-white font-medium">Cookie analitici</div>
                    <p className="text-xs text-slate-400 mt-1">Statistiche anonime e aggregate su come viene utilizzato il sito.</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={analytics}
                    onClick={() => setAnalytics((v) => !v)}
                    data-testid="cookie-toggle-analytics"
                    className={`relative h-6 w-11 rounded-full border transition ${analytics ? "bg-gradient-to-r from-purple-500 to-blue-500 border-transparent" : "bg-white/5 border-white/15"}`}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${analytics ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </label>
              </div>
            )}

            <div className="mt-6 flex flex-col md:flex-row gap-2 md:gap-3">
              {!showPrefs ? (
                <>
                  <button onClick={acceptAll} data-testid="cookie-accept-all" className="btn-primary flex-1 justify-center text-sm py-3">Accetta tutti</button>
                  <button onClick={rejectAll} data-testid="cookie-reject-all" className="btn-ghost flex-1 justify-center text-sm py-3">Rifiuta</button>
                  <button onClick={() => setShowPrefs(true)} data-testid="cookie-open-prefs" className="text-xs text-slate-400 hover:text-white underline underline-offset-4 py-3">
                    Preferenze
                  </button>
                </>
              ) : (
                <>
                  <button onClick={save} data-testid="cookie-save-prefs" className="btn-primary flex-1 justify-center text-sm py-3">Salva preferenze</button>
                  <button onClick={() => setShowPrefs(false)} data-testid="cookie-back" className="btn-ghost text-sm py-3 px-4">
                    Indietro
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
